import React, { useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DropdownSelect,
  IconButton,
  LANGUAGE_ENGLISH_NAMES,
  makeStyles
} from '@paintscout/ui'
import type { DialogProps, DropdownSelectOption, SupportedLang } from '@paintscout/ui'
import type { TranslationCompletionStatus } from '@paintscout/util/builder/quote/util/translation-status'
import CloseIcon from '@material-ui/icons/Close'

const useStyles = makeStyles((theme) => ({
  overflowVisible: {
    overflow: 'visible'
  },
  alert: { marginTop: theme.spacing() }
}))

export interface DownloadWorkOrderDialogProps extends Omit<DialogProps, 'onConfirm'> {
  availableLangs: string[]
  /**
   * Per-language pre-bake status. If the selected language is not `complete`, the download
   * is blocked and the contractor is warned to generate the translation first.
   */
  translationStatus?: Record<string, TranslationCompletionStatus>
  onConfirm: (lang?: string) => void
  onCancel: () => void
}

const ENGLISH_OPTION: DropdownSelectOption = {
  label: LANGUAGE_ENGLISH_NAMES['en'],
  value: 'en'
}

function DownloadWorkOrderDialog({
  availableLangs,
  translationStatus = {},
  onConfirm,
  onCancel,
  ...dialogProps
}: DownloadWorkOrderDialogProps) {
  const classes = useStyles()
  const langOptions: DropdownSelectOption[] = [
    ENGLISH_OPTION,
    ...availableLangs.map((lang) => ({
      label: LANGUAGE_ENGLISH_NAMES[lang as SupportedLang],
      value: lang
    }))
  ]

  const [selectedLang, setSelectedLang] = useState<DropdownSelectOption>(ENGLISH_OPTION)

  // English never needs generation; a non-English language must be pre-baked ('complete')
  // before its PDF can be downloaded, otherwise the contractor would get untranslated content.
  const needsGeneration = selectedLang.value !== 'en' && translationStatus[selectedLang.value] !== 'complete'

  function handleConfirm() {
    if (needsGeneration) return
    onConfirm(selectedLang.value === 'en' ? undefined : selectedLang.value)
  }

  return (
    <Dialog {...dialogProps} classes={{ paper: classes.overflowVisible }}>
      <DialogTitle
        rightContent={
          <IconButton size="small" onClick={onCancel}>
            <CloseIcon />
          </IconButton>
        }
      >
        Download Work Order
      </DialogTitle>
      <DialogContent className={classes.overflowVisible}>
        <DropdownSelect
          variant="single"
          label="Language"
          options={langOptions}
          value={selectedLang}
          onChange={(option) => setSelectedLang(option as DropdownSelectOption)}
        />
        {needsGeneration && (
          <Alert severity="warning" className={classes.alert}>
            {LANGUAGE_ENGLISH_NAMES[selectedLang.value as SupportedLang] ?? selectedLang.value} has not been generated
            yet. Use the Generate Translation option first, otherwise the download would contain untranslated content.
          </Alert>
        )}
      </DialogContent>
      <DialogActions
        leftButton={
          <Button variant="text" onClick={onCancel}>
            Cancel
          </Button>
        }
      >
        <Button variant="contained" color="primary" onClick={handleConfirm} disabled={needsGeneration}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DownloadWorkOrderDialog
